import { createInstance, type i18n as I18nInstance, type BackendModule, type InitOptions } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

const SUPPORTED_LANGS = ['en', 'zh'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGS)[number];

const PUBLIC_PATH = '/locales';

const fetchBackend: BackendModule = {
  type: 'backend',
  init: () => undefined,
  read(language, namespace, callback) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);

    fetch(`${PUBLIC_PATH}/${language}/${namespace}.json`, {
      signal: controller.signal,
      headers: { 'Content-Type': 'application/json' }
    })
      .then(async (response) => {
        clearTimeout(timeout);
        if (!response.ok) {
          throw new Error(`Failed to load i18n resources: ${response.status}`);
        }
        const json = (await response.json()) as Record<string, unknown>;
        callback(null, json);
      })
      .catch((error) => {
        clearTimeout(timeout);
        callback(error as Error, null);
      });

    return () => controller.abort();
  }
};

const defaultOptions: InitOptions = {
  fallbackLng: 'en',
  supportedLngs: [...SUPPORTED_LANGS],
  load: 'languageOnly',
  ns: ['translation'],
  defaultNS: 'translation',
  interpolation: { escapeValue: false },
  detection: {
    order: ['localStorage', 'navigator', 'htmlTag'],
    caches: ['localStorage']
  }
};

let sharedInstance: I18nInstance | null = null;

export async function initI18n(): Promise<I18nInstance> {
  if (sharedInstance?.isInitialized) {
    return sharedInstance;
  }

  const instance = sharedInstance ?? createInstance();
  sharedInstance = instance;

  if (!instance.isInitialized) {
    await instance
      .use(LanguageDetector)
      .use(fetchBackend)
      .use(initReactI18next)
      .init(defaultOptions);
  }

  return instance;
}

export function getI18nInstance(): I18nInstance | null {
  return sharedInstance;
}

export const supportedLanguages = SUPPORTED_LANGS;
